/**
 * Meta Pixel wrapper — fbq seguro em SSR/dev/adblock.
 *
 * O snippet base é injetado em index.html (carrega async, define fbq global).
 * Esse wrapper só dispara eventos quando fbq existe — não quebra se o user
 * tiver adblock, se o snippet falhou em carregar, ou se rodando SSR.
 *
 * eventId é usado pra deduplicar com a API de Conversões (server-side).
 * Sobral A10 traqueamento: "É você ter um pixel instalado + API de conversões + GA4."
 */

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

/** Eventos padrão Meta usados pelo Pórtico. */
export type MetaStandardEvent =
  | 'PageView'
  | 'ViewContent'
  | 'Lead'
  | 'CompleteRegistration'
  | 'Subscribe'
  | 'StartTrial';

interface MetaEventOptions {
  /** ID determinístico (ex: user_id) pra deduplicar com CAPI. */
  eventID?: string;
  /** Valor monetário do evento (Subscribe/Purchase). */
  value?: number;
  /** Moeda do value (default BRL). */
  currency?: string;
  /** Properties custom anexadas ao evento. */
  [key: string]: unknown;
}

export function trackMetaEvent(
  event: MetaStandardEvent,
  options: MetaEventOptions = {}
): void {
  if (typeof window === 'undefined') return;
  if (typeof window.fbq !== 'function') return;
  const { eventID, value, currency, ...custom } = options;
  const params: Record<string, unknown> = { ...custom };
  if (typeof value === 'number') {
    params.value = value;
    params.currency = currency ?? 'BRL';
  }
  try {
    if (eventID) {
      window.fbq('track', event, params, { eventID });
    } else {
      window.fbq('track', event, params);
    }
  } catch {
    /* non-fatal */
  }
}

export function trackMetaCustomEvent(
  eventName: string,
  options: MetaEventOptions = {}
): void {
  if (typeof window === 'undefined') return;
  if (typeof window.fbq !== 'function') return;
  const { eventID, ...custom } = options;
  try {
    if (eventID) {
      window.fbq('trackCustom', eventName, custom, { eventID });
    } else {
      window.fbq('trackCustom', eventName, custom);
    }
  } catch {
    /* non-fatal */
  }
}
